import { useMemo, useState } from "preact/hooks";
import { PRESET_STYLES } from "../config/presets";
import { useExternalAssets } from "../hooks/useExternalAssets";
import { TRACKED_PARAMS, usePageParams } from "../hooks/usePageParams";
import { type Slot } from "../queries/useCampaignAvailability";
import { ConfirmStep } from "./ConfirmStep";
import { browserTimezone, COMMON_TIMEZONES } from "./format";
import { SelectStep } from "./SelectStep";
import { SiteHeader } from "./SiteHeader";
import { SuccessStep } from "./SuccessStep";

type Step =
	| { kind: "select" }
	| { kind: "confirm"; slot: Slot; advisorName: string; advisorId: number }
	| {
			kind: "success";
			slot: Slot;
			advisorName: string;
			leadName: string;
			leadEmail: string;
	  };

const MAIA_STYLES = PRESET_STYLES.maia;

export default function BookConsultation() {
	const { isValidPage, backgroundColorOverride, logoUrl, urlParams } =
		usePageParams();

	useExternalAssets(MAIA_STYLES);

	const campaignId = urlParams.campaign_id ?? "";

	const attribution: Record<string, string> = {};
	for (const key of TRACKED_PARAMS) {
		const value = urlParams[key];
		if (value) attribution[key] = value;
	}

	const [timezone, setTimezone] = useState<string>(browserTimezone());
	const [step, setStep] = useState<Step>({ kind: "select" });

	const tzOptions = useMemo(() => {
		const set = new Set([...COMMON_TIMEZONES, timezone]);
		return Array.from(set).sort();
	}, [timezone]);

	const pageStyle = backgroundColorOverride
		? { backgroundColor: backgroundColorOverride }
		: undefined;

	const containerClassName =
		"min-h-screen flex items-center justify-center p-4 bg-[#e0e1b7] text-[#17372e]";

	if (isValidPage === null) {
		return <div className={containerClassName} style={pageStyle} />;
	}

	if (!isValidPage) {
		return (
			<div className={containerClassName} style={pageStyle}>
				<div className="text-center">
					<h1 className="text-2xl mb-4">Page Not Found</h1>
					<p className="text-base opacity-80">
						This page does not exist or the link is invalid.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-[#e0e1b7] text-[#17372e]" style={pageStyle}>
			<SiteHeader logoUrl={logoUrl} />

			<main className="mx-auto max-w-3xl px-6 pb-24 pt-12 sm:px-10">
				{step.kind === "select" && (
					<SelectStep
						campaignId={campaignId}
						timezone={timezone}
						tzOptions={tzOptions}
						onTimezoneChange={setTimezone}
						onPick={(slot, advisorName, advisorId) =>
							setStep({ kind: "confirm", slot, advisorName, advisorId })
						}
					/>
				)}
				{step.kind === "confirm" && (
					<ConfirmStep
						slot={step.slot}
						timezone={timezone}
						advisorName={step.advisorName}
						advisorId={step.advisorId}
						campaignId={campaignId}
						attribution={attribution}
						onBack={() => setStep({ kind: "select" })}
						onSuccess={(leadName, leadEmail) =>
							setStep({
								kind: "success",
								slot: step.slot,
								advisorName: step.advisorName,
								leadName,
								leadEmail,
							})
						}
					/>
				)}
				{step.kind === "success" && (
					<SuccessStep
						slot={step.slot}
						timezone={timezone}
						advisorName={step.advisorName}
						leadEmail={step.leadEmail}
					/>
				)}
			</main>
		</div>
	);
}
